import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/utils";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Render the card's styling onto the child element instead of a `<div>` —
   * for a card that has to be a `<section>`, an `<article>` or an `<aside>`
   * so it keeps its landmark and its `aria-labelledby`.
   */
  asChild?: boolean;
}

/**
 * **The** card. There is one card surface in this app and this is it — every
 * property list, section block, settings block and panel card is this
 * component, so a card never reads as one kind of thing on the project rail
 * and another on the task rail beside it.
 *
 * `rounded-lg bg-panel p-4`. That is the fill the app's own cards already
 * converged on — the two detail rails, the settings sections, the sub-task and
 * milestone lists — reached independently and then copied file to file as a
 * bare class string. This component is where that string lives now; the copies
 * are gone. (It used to render `bg-sunken` while its doc comment claimed
 * `bg-panel` + `shadow-raised`, and only billing ever mounted it, which is how
 * the app ended up with two card fills at once.)
 *
 * A card never nests inside another card — the boundary between two identical
 * fills is invisible. Do NOT wrap a page, a list, a table, a toolbar or an
 * empty state in one; separate those with space and an eyebrow:
 *
 *   <section className="pt-3">
 *     <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">…</h3>
 *   </section>
 *
 * Not for: the app frame and the settings frame (they are the sheet the cards
 * sit on), kanban tiles (`KanbanCard`, which carries drag chrome and its own
 * `dark:bg-elevated` step), sticky bars (§2.1 — they keep an opaque fill to
 * occlude what scrolls under them), or chat bubbles.
 */
/**
 * The card surface as a class, for the handful of places that cannot mount a
 * `<Card>` because they are styling **someone else's** root — a `ListSection`
 * or a section component that takes a `className`. Wrapping those in a Card
 * would put a card inside a card, which is the one thing §2 forbids.
 *
 * Fill, radius and ink only — **no padding**. `<Card>` adds the default `p-4`
 * on top of this; a call site that borrows the surface for someone else's root
 * is padding that root itself and would otherwise get it twice.
 */
export const cardSurfaceClass = "rounded-lg bg-panel text-text";

/**
 * The card's own gutter, and the only one in the component. `p-4` — the same
 * 16px the rail cards, the settings sections and the properties lists had each
 * arrived at by hand, so a card is padded once, by the card.
 *
 * The parts (`CardHeader`, `CardContent`, `CardFooter`) carry **no** padding
 * and no margin: they used to each add their own `p-6`, which meant the gutter
 * you got depended on which parts you happened to mount, and any call site
 * wanting a full-bleed row list had to cancel three of them. They are layout
 * only now — a header is a column, a footer is a row.
 *
 * A card that must run its content edge to edge (a row list that pads its own
 * rows) says so with `p-0`.
 */
const CARD_PADDING = "p-4";

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    return (
      <Comp
        ref={ref}
        data-slot="card"
        className={cn(cardSurfaceClass, CARD_PADDING, className)}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1.5", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-xs font-semibold text-text", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-xs text-text-secondary", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-2", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
